import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Activity, Calendar, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { format } from 'date-fns';

interface SymptomEntry {
  id: string;
  tracked_at: string;
  symptom_intensity: number;
  symptom_details: any;
  has_new_symptoms: boolean;
  functional_capacity: number;
  notes: string;
}

export const SymptomHistory = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<SymptomEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchSymptomHistory();
    }
  }, [user]);

  const fetchSymptomHistory = async () => {
    if (!user) return;

    try {
      const { data, error } = await (supabase as any)
        .from('daily_symptom_tracker')
        .select('*')
        .eq('user_id', user.id)
        .order('tracked_at', { ascending: false })
        .limit(30);

      if (error) throw error;

      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching symptom history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIntensityColor = (intensity: number) => {
    if (intensity <= 3) return 'bg-green-100 text-green-800';
    if (intensity <= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getTrend = (currentIndex: number) => {
    if (currentIndex >= entries.length - 1) return null;
    
    const current = entries[currentIndex].symptom_intensity;
    const previous = entries[currentIndex + 1].symptom_intensity;
    
    if (current > previous) return <TrendingUp className="h-4 w-4 text-red-500" />;
    if (current < previous) return <TrendingDown className="h-4 w-4 text-green-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const calculateAverageIntensity = () => {
    if (entries.length === 0) return 0;
    const sum = entries.reduce((acc, entry) => acc + entry.symptom_intensity, 0);
    return (sum / entries.length).toFixed(1);
  };

  const getRecentTrend = () => {
    if (entries.length < 7) return 'Insufficient data';
    
    const recent = entries.slice(0, 7);
    const older = entries.slice(7, 14);
    
    const recentAvg = recent.reduce((acc, entry) => acc + entry.symptom_intensity, 0) / recent.length;
    const olderAvg = older.reduce((acc, entry) => acc + entry.symptom_intensity, 0) / older.length;
    
    if (recentAvg < olderAvg - 0.5) return 'Improving';
    if (recentAvg > olderAvg + 0.5) return 'Worsening';
    return 'Stable';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Symptom Tracking Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{entries.length}</div>
              <div className="text-sm text-blue-700">Total Entries</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{calculateAverageIntensity()}</div>
              <div className="text-sm text-green-700">Avg Intensity</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{getRecentTrend()}</div>
              <div className="text-sm text-purple-700">7-Day Trend</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entry History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-600" />
            Recent Entries
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No symptom tracking entries yet.</p>
              <p className="text-sm">Start tracking your daily symptoms to see your history here.</p>
            </div>
          ) : (
            entries.map((entry, index) => (
              <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Badge className={getIntensityColor(entry.symptom_intensity)}>
                      {entry.symptom_intensity}/10
                    </Badge>
                    {getTrend(index)}
                  </div>
                  <div>
                    <div className="font-medium text-sm">
                      {format(new Date(entry.tracked_at), 'MMM dd, yyyy')}
                    </div>
                    <div className="text-xs text-gray-600">
                      {format(new Date(entry.tracked_at), 'h:mm a')}
                    </div>
                  </div>
                </div>
                
                <div className="text-right text-sm">
                  {entry.symptom_details?.location && (
                    <div className="text-gray-700">{entry.symptom_details.location}</div>
                  )}
                  {entry.symptom_details?.timing && (
                    <div className="text-xs text-gray-500">{entry.symptom_details.timing}</div>
                  )}
                  {entry.has_new_symptoms && (
                    <Badge variant="destructive" className="text-xs mt-1">New Symptoms</Badge>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {entries.length > 0 && (
        <div className="text-center">
          <Button 
            variant="outline" 
            onClick={fetchSymptomHistory}
            className="w-full"
          >
            Refresh History
          </Button>
        </div>
      )}
    </div>
  );
};